import { Component, OnInit } from '@angular/core';
import { UtilDialogService } from '@core';
import { Account } from '@models';
import { UtilFns } from '@utils';
import { ToastrService } from 'ngx-toastr';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom } from 'rxjs';

export interface IProgramNewChoicesParams {
  account: Account;
  availableAfterJoinDateDays: number;
  startDate: Date | null;
  endDate: Date | null;
  durationInDays: number;
  programType: string;
}

@Component({
  selector: 'prox-program-new-choices.dialog',
  standalone: false,
  templateUrl: './program-new-choices.dialog.component.html',
})
export class ProgramNewChoicesDialogComponent implements OnInit {
  account!: Account;
  isPageReady = false;
  img = 'assets/proximity/timeframe.png';
  showFF = false;
  showVV = false;
  showVF = false;
  showInv = false;

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig<IProgramNewChoicesParams>,
    public toastr: ToastrService,
    public dialogService: UtilDialogService,
  ) {}

  ngOnInit(): void {
    const params = this.config.data;
    UtilFns.assertNonNull(params);
    this.account = params?.account;
    this.isPageReady = true;
  }

  public static async openDialog(dialogService: DialogService, data?: IProgramNewChoicesParams, config?: DynamicDialogConfig) {
    const ref = dialogService.open(ProgramNewChoicesDialogComponent, {
      closable: false,
      draggable: true,
      width: '80vh',
      height: '90vh',
      maximizable: true,
      resizable: true,
      // baseZIndex: 10000,
      focusOnShow: false,
      ...config,
      data: data,
    });
    const r = await firstValueFrom(ref.onClose);
    return r;
  }

  onSelect(programType: string) {
    this.onCancelSelect();
    if (programType === 'ff') {
      this.showFF = true;
    }
    if (programType === 'vv') {
      this.showVV = true;
    }
    if (programType === 'vf') {
      this.showVF = true;
    }
    if (programType === 'inv') {
      // No Account-Owned Inventory Management check here any more. The gate belongs on
      // ACTIVATION, not creation - see program-list.onChangeActiveStatus and
      // program-frm.tryProgramActivate. An Inventory Program may now be built on Hold before
      // the partnership that permits it is switched on; it simply cannot go Active until then.
      this.showInv = true;
    }
  }

  async onContinue(programType: string) {
    const errs: string[] = [];
    if (this.config.data) {
      if (this.config.data.availableAfterJoinDateDays < 0) {
        errs.push('Available # days after join date must be greater than zero');
      }
    }
    if (programType === 'ff') {
      if (this.config.data && this.config.data.startDate && this.config.data.endDate) {
        if (this.config.data.startDate.getTime() < new Date().setHours(0, 0, 0, 0)) {
          errs.push('Start date must be equal to or greater than today');
        }

        if (this.config.data.endDate.getTime() <= this.config.data.startDate.getTime()) {
          errs.push('End date must be greater than start date');
        }
      }
    }

    if (programType === 'vv') {
      if (this.config.data) {
        if (this.config.data.durationInDays < 1) {
          errs.push('Program duration must be greater than 1 day');
        }
      }
    }

    if (programType === 'vf') {
      if (this.config.data && this.config.data.endDate) {
        if (this.config.data.endDate.getTime() <= new Date().getTime()) {
          errs.push('End date must be greater than today');
        }
      }
    }

    if (programType === 'inv') {
      if (this.config.data) {
        this.config.data.startDate = null;
        this.config.data.endDate = null;
      }
    }

    if (errs.length > 0) {
      for (const p of errs) {
        await this.dialogService.warnDialog('Cannot Continue', p);
      }
      return;
    }

    if (this.config.data) {
      this.config.data.programType = programType;
    }
    this.ref.close(this.config?.data);
  }

  onCancelSelect() {
    if (this.config.data) {
      this.config.data.availableAfterJoinDateDays = 0;
      this.config.data.durationInDays = 90;
      this.config.data.startDate = new Date();
      this.config.data.endDate = new Date(Date.now() + 364 * 24 * 60 * 60 * 1000);
    }
    this.showFF = false;
    this.showVV = false;
    this.showVF = false;
    this.showInv = false;
  }

  onCancel() {
    this.ref.close(null);
  }
}
